import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Download, Printer, Tag, ZoomIn, ZoomOut } from '@/components/icons';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import LabelPrintPreview from '@/components/haccp/LabelPrintPreview';
import LabelVersionControls from '@/components/haccp/LabelVersionControls';
import useLabelVersions from '@/components/haccp/useLabelVersions';
import EntityBadge from '@/components/haccp/EntityBadge';
import ProductTypeBadge from '@/components/haccp/ProductTypeBadge';
import { LoadingState, ErrorState } from '@/components/haccp/AsyncState';
import useDirectPrinters from '@/hooks/useDirectPrinters';
import {
  PAPER_PRESETS,
  buildPrintJob,
  createPrintPdf,
  downloadPdf,
  getPrintGeometry,
} from '@/lib/labelPrint';
import { connectDirectPrinter, printLabel } from '@/lib/printing/printManager';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PREVIEW_PX_PER_MM = 96 / 25.4;
const PREVIEW_INSET_PX = 32;
const PREVIEW_FIT_WIDTH_RATIO = 0.80;
const PREVIEW_FIT_HEIGHT_RATIO = 0.78;
const PREVIEW_MAX_WIDTH_PX = 760;
const MIN_PREVIEW_SCALE = 0.2;
const MAX_PREVIEW_SCALE = 10;

export default function LabelPrintDialog({ open, onOpenChange, lotId, productName, productType, lotNumber }) {
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState('');
  const [context, setContext] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copies, setCopies] = useState(1);
  const [paper, setPaper] = useState('label');
  const [orientation, setOrientation] = useState('portrait');
  const [margin, setMargin] = useState(0);
  const [gap, setGap] = useState(2);
  const [customW, setCustomW] = useState(210);
  const [customH, setCustomH] = useState(297);
  const [savingPdf, setSavingPdf] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [zoom, setZoom] = useState(1);
  const previewViewportRef = useRef(null);
  const [previewViewportSize, setPreviewViewportSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    if (!open || !lotId) return undefined;
    let alive = true;
    (async () => {
      setLoading(true); setError(''); setContext(null);
      try {
        const [t, c] = await Promise.all([
          fetch(`${BACKEND_URL}/api/label-templates/`, { credentials: 'include' }),
          fetch(`${BACKEND_URL}/api/labels/preview-context/${lotId}`, { credentials: 'include' }),
        ]);
        if (!t.ok) throw new Error('Impossibile caricare i template etichetta');
        if (!c.ok) throw new Error((await c.json().catch(() => ({}))).detail || 'Anteprima non disponibile');
        const tpl = (await t.json()).filter(x => !x.design_only);
        const ctx = await c.json();
        if (!alive) return;
        setTemplates(tpl);
        setTemplateId(prev => tpl.some(x => x.template_id === prev) ? prev : (tpl[0]?.template_id || ''));
        setContext(ctx);
      } catch (e) {
        if (alive) setError(e.message);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [open, lotId]);

  useEffect(() => {
    if (!open) {
      setCopies(1); setPaper('label'); setOrientation('portrait'); setMargin(0); setGap(2); setZoom(1);
      setPrinting(false); setSavingPdf(false);
    }
  }, [open]);

  useEffect(() => {
    if (open) setZoom(1);
  }, [open, templateId, paper, orientation, customW, customH]);

  const template = templates.find(t => t.template_id === templateId);
  const version = useLabelVersions({ backendUrl: BACKEND_URL, lotId: open ? lotId : null, templateId, context, layout: template?.layout });
  const layout = version.layout;
  const geometry = useMemo(() => getPrintGeometry({ layout, paper, orientation, margin, gap, customW, customH }), [layout, paper, orientation, margin, gap, customW, customH]);
  useEffect(() => {
    if (!open || loading || error) return undefined;
    const viewport = previewViewportRef.current;
    if (!viewport) return undefined;

    const updateSize = () => {
      const next = { width: viewport.clientWidth, height: viewport.clientHeight };
      setPreviewViewportSize(prev => (prev.width === next.width && prev.height === next.height ? prev : next));
    };

    updateSize();
    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(updateSize);
      observer.observe(viewport);
      return () => observer.disconnect();
    }

    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, [open, loading, error]);

  const previewFitScale = useMemo(() => {
    const paperWidthPx = Math.max(Number(geometry.finalPaper.width) || 0, 1) * PREVIEW_PX_PER_MM;
    const paperHeightPx = Math.max(Number(geometry.finalPaper.height) || 0, 1) * PREVIEW_PX_PER_MM;
    const availableWidth = Math.max(previewViewportSize.width - PREVIEW_INSET_PX, 0);
    const availableHeight = Math.max(previewViewportSize.height - PREVIEW_INSET_PX, 0);
    if (!availableWidth || !availableHeight) return 1;

    // Fit into a comfortable visual frame instead of stretching the label edge-to-edge.
    // The print size stays unchanged: this affects preview magnification only.
    const targetWidth = Math.min(availableWidth * PREVIEW_FIT_WIDTH_RATIO, PREVIEW_MAX_WIDTH_PX);
    const targetHeight = availableHeight * PREVIEW_FIT_HEIGHT_RATIO;
    const fit = Math.min(targetWidth / paperWidthPx, targetHeight / paperHeightPx);
    return Math.max(MIN_PREVIEW_SCALE, Math.min(MAX_PREVIEW_SCALE, fit));
  }, [geometry.finalPaper.width, geometry.finalPaper.height, previewViewportSize]);
  const previewScale = previewFitScale * zoom;
  const directEligible = geometry.fits && geometry.labelsPerPage === 1;
  const direct = useDirectPrinters({
    backendUrl: BACKEND_URL,
    widthMm: geometry.finalPaper.width,
    heightMm: geometry.finalPaper.height,
    enabled: open && directEligible,
  });

  const makeJob = (printerId, versionId) => ({ ...buildPrintJob({ lotId, templateId, paper, copies, geometry, printerId }), version_id: versionId });

  const print = async () => {
    if (!lotId || !templateId || !layout || !version.data || version.loading || version.error || version.editing) return toast.error('Anteprima di stampa non pronta');
    if (!geometry.fits) return toast.error('Il supporto selezionato è più piccolo dell’etichetta');
    if (geometry.labelsPerPage !== 1) return toast.error('Per i fogli con più etichette usa Salva PDF');
    if (!direct.selectedId || !direct.selected?.available) return toast.error('Nessuna stampante disponibile compatibile con questo formato');
    setPrinting(true);
    try {
      await connectDirectPrinter(direct.selected);
      const versionId = await version.ensureVersion();
      const result = await printLabel(BACKEND_URL, makeJob(direct.selectedId, versionId), direct.selected);
      toast.success(`${result.copies} ${result.copies === 1 ? 'etichetta inviata' : 'etichette inviate'} a ${result.printer?.name || 'stampante'}`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setPrinting(false);
    }
  };

  const savePdf = async () => {
    if (!lotId || !templateId || !layout || !version.data || version.loading || version.error || version.editing) return toast.error('Template non disponibile');
    if (!geometry.fits) return toast.error('Il supporto selezionato è più piccolo dell’etichetta');
    setSavingPdf(true);
    try {
      const versionId = await version.ensureVersion();
      const data = await createPrintPdf(BACKEND_URL, makeJob(null, versionId));
      const cleanLot = String(lotNumber || version.data?.lot_number || 'lotto').replace(/[^a-zA-Z0-9_-]+/g, '_');
      await downloadPdf(BACKEND_URL, data.label_id, `etichetta_${cleanLot}.pdf`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSavingPdf(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent overlayClassName="!z-[100]" className="!flex !top-3 !bottom-3 !translate-y-0 !z-[110] !w-[94vw] !max-w-[1440px] !h-auto !max-h-none min-h-0 overflow-hidden !p-0 !gap-0 flex-col sm:!top-4 sm:!bottom-4 sm:rounded-xl">
        <div className="shrink-0 border-b bg-white px-5 py-4 pr-16">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 leading-tight"><Printer className="h-5 w-5 shrink-0" />Anteprima di stampa</DialogTitle>
          </DialogHeader>
          {productName && (
            <div className="mt-2 flex min-w-0 flex-wrap items-center gap-2 text-sm">
              <span className="max-w-[min(420px,70vw)] truncate font-medium" title={productName}>{productName}</span>
              {productType && <ProductTypeBadge type={productType} compact />}
              {lotNumber && <EntityBadge entity="lot" label={lotNumber} compact />}
            </div>
          )}
        </div>
        {loading ? <div className="p-8"><LoadingState compact /></div> : error ? <div className="p-8"><ErrorState description={error} /></div> : (
          <div className="grid flex-1 min-h-0 grid-cols-1 grid-rows-[auto_minmax(360px,1fr)] overflow-y-auto md:grid-cols-[340px_minmax(0,1fr)] md:grid-rows-1 md:overflow-hidden">
            <aside className="min-h-0 border-b bg-white md:border-b-0 md:border-r flex flex-col">
              <div className="p-4 grid content-start gap-3 md:flex-1 md:min-h-0 md:overflow-y-auto">
                <div>
                  <Label>Template</Label>
                  <Select value={templateId || undefined} onValueChange={setTemplateId}>
                    <SelectTrigger className="h-9"><SelectValue placeholder="Scegli template" /></SelectTrigger>
                    <SelectContent className="!z-[120]">{templates.map(t => <SelectItem key={t.template_id} value={t.template_id}>{t.name} · {t.layout?.width_mm || 60}×{t.layout?.height_mm || 40} mm</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label>Copie</Label><Input className="h-9" type="number" min="1" max="500" value={copies} onChange={e => setCopies(Math.max(1, Math.min(500, Number(e.target.value) || 1)))} /></div>
                  <div><Label>Distanza mm</Label><Input className="h-9" type="number" min="0" max="20" step="0.5" value={paper === 'label' ? 0 : gap} disabled={paper === 'label'} onChange={e => setGap(Math.max(0, Number(e.target.value) || 0))} /></div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label>Foglio / supporto</Label><Select value={paper} onValueChange={setPaper}><SelectTrigger className="h-9"><SelectValue /></SelectTrigger><SelectContent className="!z-[120]">{Object.entries(PAPER_PRESETS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent></Select></div>
                  <div><Label>Orientamento</Label><Select value={geometry.effectiveOrientation} onValueChange={setOrientation} disabled={paper === 'label'}><SelectTrigger className="h-9"><SelectValue /></SelectTrigger><SelectContent className="!z-[120]"><SelectItem value="portrait">Verticale</SelectItem><SelectItem value="landscape">Orizzontale</SelectItem></SelectContent></Select></div>
                </div>
                {paper === 'custom' && <div className="grid grid-cols-2 gap-2"><div><Label>Larghezza mm</Label><Input className="h-9" type="number" min="20" value={customW} onChange={e => setCustomW(e.target.value)} /></div><div><Label>Altezza mm</Label><Input className="h-9" type="number" min="20" value={customH} onChange={e => setCustomH(e.target.value)} /></div></div>}
                <div><Label>Margini mm</Label><Input className="h-9" type="number" min="0" max="30" step="0.5" value={paper === 'label' ? 0 : margin} disabled={paper === 'label'} onChange={e => setMargin(Math.max(0, Number(e.target.value) || 0))} /></div>
                <LabelVersionControls state={version} disabled={savingPdf || printing} selectContentClassName="!z-[120]" />

                <div className="pt-1 border-t">
                  {directEligible && <div className="pt-2"><Label>Stampante</Label><Select value={direct.selectedId || undefined} onValueChange={direct.setSelectedId} disabled={direct.loading || direct.available.length === 0}><SelectTrigger className="h-9"><SelectValue placeholder={direct.loading ? 'Verifica stampanti…' : 'Nessuna stampante disponibile'} /></SelectTrigger><SelectContent className="!z-[120]">{direct.printers.map(p => <SelectItem key={p.printer_id} value={p.printer_id} disabled={!p.available}>{p.name} · {p.label || (p.available ? 'Disponibile' : 'Non disponibile')}{p.is_default ? ' · Predefinita' : ''}</SelectItem>)}</SelectContent></Select></div>}
                </div>

                {!geometry.fits && <Alert variant="destructive" className="py-2"><AlertDescription className="text-[11px]">Il supporto selezionato non contiene l’etichetta ({geometry.labelW}×{geometry.labelH} mm).</AlertDescription></Alert>}
                {geometry.fits && geometry.labelsPerPage > 1 && <Alert className="py-2"><Tag className="h-4 w-4" /><AlertDescription className="text-[11px] leading-4">Foglio multiplo: usa Salva PDF. La stampa diretta è riservata ai supporti etichetta singoli.</AlertDescription></Alert>}
                {directEligible && !direct.loading && direct.available.length === 0 && <Alert variant="destructive" className="py-2"><AlertDescription className="text-[11px] leading-4">Nessuna stampante disponibile per {geometry.finalPaper.width}×{geometry.finalPaper.height} mm. Configurala in Opzioni → Stampanti.</AlertDescription></Alert>}
                {directEligible && direct.selected && <Alert className="py-2"><Printer className="h-4 w-4" /><AlertDescription className="text-[11px] leading-4">Stampa diretta su <strong>{direct.selected.name}</strong>: un click, senza popup o dialog del browser.</AlertDescription></Alert>}
              </div>

              <div className="shrink-0 border-t bg-white p-4 grid grid-cols-2 gap-2">
                <Button className="h-10" variant="outline" onClick={savePdf} disabled={savingPdf || printing || !templateId || version.loading || !!version.error || version.editing}><Download />{savingPdf ? 'Creo PDF…' : 'Salva PDF'}</Button>
                <Button className="h-10" onClick={print} disabled={printing || savingPdf || !directEligible || !direct.selectedId || !templateId || !version.data || version.loading || !!version.error || version.editing}><Printer />{printing ? 'Invio…' : 'Stampa'}</Button>
              </div>
            </aside>

            <main className="min-h-[360px] md:min-h-0 overflow-hidden bg-slate-50 flex flex-col p-4 md:p-5">
              <div className="mb-3 shrink-0 flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-medium">Anteprima</p>
                  <p className="truncate text-xs text-muted-foreground">{copies} {Number(copies) === 1 ? 'copia' : 'copie'} · {geometry.finalPaper.width}×{geometry.finalPaper.height} mm · {geometry.labelsPerPage} per pagina</p>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <Button type="button" variant="outline" size="icon" className="h-9 w-9" onClick={() => setZoom(z => Math.max(.5, Math.round((z - .1) * 10) / 10))} disabled={zoom <= .5} aria-label="Riduci zoom"><ZoomOut className="h-4 w-4" /></Button>
                  <Button type="button" variant="outline" className="h-9 min-w-[72px] px-2 text-xs" onClick={() => setZoom(1)} aria-label="Adatta anteprima allo spazio disponibile">{zoom === 1 ? 'Adatta' : `${Math.round(zoom * 100)}%`}</Button>
                  <Button type="button" variant="outline" size="icon" className="h-9 w-9" onClick={() => setZoom(z => Math.min(2, Math.round((z + .1) * 10) / 10))} disabled={zoom >= 2} aria-label="Aumenta zoom"><ZoomIn className="h-4 w-4" /></Button>
                </div>
              </div>
              <div ref={previewViewportRef} className="flex-1 min-h-0 rounded-xl border border-slate-200 bg-slate-100/80 p-4 overflow-auto">
                <LabelPrintPreview layout={layout} data={version.data} copies={copies} geometry={geometry} scale={previewScale} />
              </div>
            </main>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
